import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  SubstationCrudCreate,
  SubstationStats,
} from '../models/substation.model';
import { ResponsePackage } from '../models/util.model';
import { AssetStatusEnum } from '../models/asset-status.model';
import { SectionInstance } from '../models/section-definition.model';
import { Filter, PageInfo } from '../models/dynamic-filter.model';
import { EmcSearchOutput } from '../models/emc-search.model';
import { DynamicStats } from '../models/entity-model-customization.model';
import { SHARED_LIB_CONFIG, SharedLibConfig } from '../config/lib-config';

@Injectable({
  providedIn: 'root',
})
export class SubstationService {
  private pathApi: string;
  constructor(private http: HttpClient, @Inject(SHARED_LIB_CONFIG) private config: SharedLibConfig) {
    this.pathApi = this.config.pathApi + 'substation/';
  }

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  getByGuid(guid: string): Observable<any> {
    return this.http.get(`${this.pathApi}${guid}`, { observe: 'response' });
  }

  getAll(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getAll`, dataIn, {
      observe: 'response',
    });
  }

  getAllDynamic(dataIn: Filter): Observable<any> {
    dataIn.filterParams.selectedAreas =
      dataIn.filterParams.selectedAreaModels?.map(a => a.id) || [];

    dataIn.filterParams.selectedUsers =
      dataIn.filterParams.selectedUserModels?.map(u => u.id) || [];

    dataIn.filterParams.selectedTransmissionStations =
      dataIn.filterParams.selectedTsModels?.map(ts => ts.id) || [];

    return this.http.post(`${this.pathApi}getAllDynamic`, dataIn, {
      observe: 'response',
    });
  }

  getAllTagged(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getAllTagged`, dataIn, {
      observe: 'response',
    });
  }
  getInstancesForAsset(entityType: number, assetGuid: string, substationTemplateId?: number | null) {
    return this.http.get<ResponsePackage<SectionInstance[]>>(
      `${this.pathApi}getInstancesForAsset/${entityType}/${assetGuid}/${substationTemplateId ? substationTemplateId : ''}`,
      { headers: this.headers, observe: 'response' }
    );
  }
  save(ss: SubstationCrudCreate): Observable<any> {
    return this.http.post(`${this.pathApi}save`, ss, { observe: 'response' });
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.pathApi}delete/${id}`, {
      observe: 'response',
    });
  }

  download(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}downloadFile`, dataIn);
  }

  downloadDynamic(originalFilter: Filter): Observable<any> {
    const filterCopy: Filter = {
      pageInfo: new PageInfo(-1, -1),
      filterParams: {
        ...originalFilter.filterParams,
        selectedAreas: originalFilter.filterParams.selectedAreaModels?.map(a => a.id) || [],
        selectedUsers: originalFilter.filterParams.selectedUserModels?.map(u => u.id) || [],
        selectedTransmissionStations: originalFilter.filterParams.selectedTsModels?.map(ts => ts.id) || [],
      },
      dynamicFilters: originalFilter.dynamicFilters.map(df => ({
        ...df
      })),
      searchObject : new EmcSearchOutput(),
      utilityId: originalFilter.utilityId,
      substationTemplateId: null,
    };

    return this.http.post(`${this.pathApi}downloadFileDynamic`, filterCopy);
  }

  getAllForSelect(utilityId: number | null = null): Observable<any> {
    if (utilityId) {
      return this.http.get(`${this.pathApi}getAllForSelect/${utilityId}`, {
        observe: 'response',
      });
    } else {
      return this.http.get(`${this.pathApi}getAllForSelect`, {
        observe: 'response',
      });
    }
  }

  getF33sBySSId(id: number): Observable<any> {
    return this.http.get(`${this.pathApi}getF33sBySSid/${id}`, {
      observe: 'response',
    });
  }

  getStats(obj: any) {
    return this.http.post<ResponsePackage<SubstationStats>>(
      `${this.pathApi}getStats`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  recall(id: number) {
    return this.http.get<ResponsePackage<any>>(`${this.pathApi}recall/${id}`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  assignSubstation(
    substationId: number,
    userId: number,
    assetStatus: AssetStatusEnum,
    isFromRejection: boolean
  ) {
    const obj = {
      entityId: substationId,
      userId: userId,
      assetStatus: assetStatus,
      isFromRejection: isFromRejection,
    };
    return this.http.post<ResponsePackage<string>>(
      `${this.pathApi}assignSs`,
      obj,
      { observe: 'response' }
    );
  }

  auditSubstation(id: number): Observable<any> {
    return this.http.post(`${this.pathApi}auditSs/${id}`, id, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getV3SubstationStats(obj: any): Observable<any> {
    return this.http.post(`${this.pathApi}getV3SubstationStats`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getStatsDynamic(obj: DynamicStats): Observable<any> {
    return this.http.post(`${this.pathApi}getStatsDynamic`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
}
